/**
 * Task domain models for משימות שינוע 99 — the work order itself, its type
 * catalogue, its priority scale and its lifecycle status.
 *
 * These shapes are the single source of truth for the task layer: the offline
 * store (`src/db/task-store.ts`), the search/filter rules
 * (`src/core/task-filters.ts`), the seed data (`src/core/mock-tasks.ts`) and
 * the tasks UI (`src/modules/tasks/`) all build on them.
 *
 * Dates and times are stored the way the form collects them, so a task never
 * depends on the reader's time zone: `date` is `YYYY-MM-DD` and both times are
 * a 24-hour `HH:MM`.
 */
import type { ISODateString } from './index';

// ── Priority ─────────────────────────────────────────────────────────

/** Ordered from the calmest to the most pressing. */
export const TASK_PRIORITIES = ['low', 'medium', 'high', 'urgent'] as const;

export type TaskPriority = (typeof TASK_PRIORITIES)[number];

export const TASK_PRIORITY_LABELS: Record<TaskPriority, string> = {
  low: 'נמוכה',
  medium: 'רגילה',
  high: 'גבוהה',
  urgent: 'דחוף',
};

/** Position on the scale (1 = lowest) — drives sorting, never eligibility. */
export const TASK_PRIORITY_RANK: Record<TaskPriority, number> = {
  low: 1,
  medium: 2,
  high: 3,
  urgent: 4,
};

/** Badge palette, in one place, so every screen colours a priority alike. */
export const TASK_PRIORITY_TONES: Record<TaskPriority, string> = {
  low: 'border-line bg-canvas text-ink/60',
  medium: 'border-brand-300 bg-brand-50 text-brand-700',
  high: 'border-amber-300 bg-amber-50 text-amber-800',
  urgent: 'border-rose-300 bg-rose-50 text-rose-700',
};

export const DEFAULT_TASK_PRIORITY: TaskPriority = 'medium';

// ── Lifecycle status ─────────────────────────────────────────────────

/**
 * The states a task moves through — two operational ones and the escape hatch.
 *
 * The lifecycle is deliberately flat: a task is either **בביצוע**, from the
 * moment it is opened, or **הושלמה**, once a manager closes it. The waiting
 * rooms the board used to keep — `pending` ("ממתינה לשיבוץ") and `assigned`
 * ("משובצת") — are gone: they described the *crew*, not the task, and a
 * dispatcher reading the board wanted one question answered ("is this running
 * or is it done?") rather than four. What used to be read off the status is now
 * derived from the convoy itself, in `src/core/effective-status.ts`.
 *
 * `cancelled` is not a third operational state: it is how a task that should
 * never have been opened leaves the board without pretending it was carried
 * out.
 */
export const TASK_STATUSES = ['in_progress', 'completed', 'cancelled'] as const;

export type TaskStatusId = (typeof TASK_STATUSES)[number];

/** The task is running — the state every task is opened in. */
export const ACTIVE_TASK_STATUS_ID: TaskStatusId = 'in_progress';

/** The task is closed — the only way off the board that counts as carried out. */
export const COMPLETED_TASK_STATUS_ID: TaskStatusId = 'completed';

/** The two states the lifecycle is actually run on, in order. */
export const OPERATIONAL_TASK_STATUSES: readonly TaskStatusId[] = [
  ACTIVE_TASK_STATUS_ID,
  COMPLETED_TASK_STATUS_ID,
];

export const TASK_STATUS_LABELS: Record<TaskStatusId, string> = {
  in_progress: 'בביצוע',
  completed: 'הושלמה',
  cancelled: 'בוטלה',
};

export const TASK_STATUS_TONES: Record<TaskStatusId, string> = {
  in_progress: 'border-amber-300 bg-amber-50 text-amber-800',
  completed: 'border-emerald-300 bg-emerald-50 text-emerald-700',
  cancelled: 'border-line bg-canvas text-ink/40',
};

export const DEFAULT_TASK_STATUS_ID: TaskStatusId = ACTIVE_TASK_STATUS_ID;

/** A task that is still on the board — the counter the dashboard leads with. */
export const OPEN_TASK_STATUSES: readonly TaskStatusId[] = [ACTIVE_TASK_STATUS_ID];

/**
 * The statuses the two-state lifecycle retired. Rows written by an older build
 * — and rows arriving from a device that has not been updated yet — still carry
 * them, so every read goes through {@link normalizeTaskStatus} rather than
 * trusting the stored string.
 */
export const LEGACY_TASK_STATUS_IDS: readonly string[] = ['pending', 'assigned'];

/**
 * The status a stored value actually means today.
 *
 * A retired status meant "opened, not finished", which is exactly what
 * `in_progress` means now, so both fold into it. Anything unreadable folds
 * there too: a task whose status cannot be parsed is still a task somebody has
 * to carry out, and dropping it off the board would be the worse failure.
 */
export function normalizeTaskStatus(value: string | null | undefined): TaskStatusId {
  const status = String(value ?? '').trim();
  return (TASK_STATUSES as readonly string[]).includes(status)
    ? (status as TaskStatusId)
    : ACTIVE_TASK_STATUS_ID;
}

/** True for a status this build no longer writes but still has to read. */
export function isLegacyTaskStatus(value: string | null | undefined): boolean {
  return LEGACY_TASK_STATUS_IDS.includes(String(value ?? '').trim());
}

/** The Hebrew label of a status, legacy values included. */
export function taskStatusLabel(value: string | null | undefined): string {
  return TASK_STATUS_LABELS[normalizeTaskStatus(value)];
}

/** The badge palette of a status, legacy values included. */
export function taskStatusTone(value: string | null | undefined): string {
  return TASK_STATUS_TONES[normalizeTaskStatus(value)];
}

/** True while the task is still being carried out. */
export function isActiveTask(task: Pick<Task, 'statusId'>): boolean {
  return normalizeTaskStatus(task.statusId) === ACTIVE_TASK_STATUS_ID;
}

/** True once a manager has closed the task. */
export function isCompletedTask(task: Pick<Task, 'statusId'>): boolean {
  return normalizeTaskStatus(task.statusId) === COMPLETED_TASK_STATUS_ID;
}

// ── Recycle bin (סל מחזור) ───────────────────────────────────────────

/**
 * How long a deleted task waits in the recycle bin before the system removes
 * it for good. The countdown starts at {@link Task.deletedAt}; the sweep itself
 * lives in `src/core/recycle-bin.ts` and runs whenever the board is loaded.
 */
export const RECYCLE_BIN_RETENTION_DAYS = 30;

export const RECYCLE_BIN_RETENTION_MS = RECYCLE_BIN_RETENTION_DAYS * 24 * 60 * 60 * 1000;

// ── Task types (סוגי משימה) ──────────────────────────────────────────

/**
 * One entry in the task-type catalogue.
 *
 * `isSystem` marks the seven types the application ships with: they are
 * protected and may not be deleted. `isDefault` marks a type as part of that
 * default catalogue — a type a manager adds from the creation form carries
 * neither flag.
 */
export interface TaskType {
  id: string;
  name: string;
  description: string;
  isDefault: boolean;
  isSystem: boolean;
  createdAt: ISODateString;
}

// ── Contact carried on the task ──────────────────────────────────────

/**
 * A one-off contact typed straight into the form. It lives on the task itself;
 * ticking "שמור איש קשר זה לשימוש עתידי" also files it in the contacts book,
 * and the task then points at that record through `contactId` instead.
 */
export interface ManualContact {
  name: string;
  phone: string;
  role?: string;
}

// ── The task ─────────────────────────────────────────────────────────

export interface Task {
  id: string;
  title: string;
  /** Points at a {@link TaskType} — seeded or manager-defined. */
  taskTypeId: string;
  description: string;
  /** Calendar day, `YYYY-MM-DD`. */
  date: string;
  /** `HH:MM`, 24-hour. */
  startTime: string;
  /** Estimated end, `HH:MM` — an estimate, never a hard stop. */
  estEndTime: string;
  priority: TaskPriority;
  /** Set when the contact was picked from the saved contacts book. */
  contactId?: string;
  /** Set when the contact was typed into the form instead. */
  manualContact?: ManualContact;
  destinationAddress?: string;
  /** Navigation link (Waze / Google Maps) as pasted by the manager. */
  destinationLink?: string;
  statusId: TaskStatusId;
  /** Operational notes — visible to everyone the task reaches. */
  operationalNotes?: string;
  /** Manager-only notes — never rendered without `VIEW_ALL_TASKS`. */
  internalNotes?: string;
  /** Id of the `User` that opened the task. */
  createdBy: string;
  /**
   * The manager's closure — deliberately not the soldier's. A crew member
   * reporting "סימון סיום ביצוע" writes their own execution row
   * (`src/types/execution.ts`); only `closeTaskAsManager()` writes these three,
   * and only that moves the task to `completed`.
   */
  closedAt?: ISODateString;
  /** Id of the `User` that closed the task. */
  closedBy?: string;
  closingNote?: string;
  /**
   * Soft-delete stamp. A task carrying one has been moved to the recycle bin
   * (סל מחזור): it is off the board, still restorable, and purged for good
   * once {@link RECYCLE_BIN_RETENTION_DAYS} have passed.
   */
  deletedAt?: ISODateString;
  /** Id of the `User` that moved the task to the recycle bin. */
  deletedBy?: string;
  createdAt: ISODateString;
  updatedAt: ISODateString;
  /**
   * The name of the operator that opened the task, and the name of the one that
   * last wrote to it — "מי עידכן מה", readable straight off the board without
   * opening the task or resolving `createdBy` against the directory.
   *
   * Names rather than ids on purpose, and frozen rather than joined: the rules
   * are stated once, in `src/core/audit-stamp.ts`.
   */
  createdByName?: string;
  updatedByName?: string;
}
